/**
 * Recipe Import Hook
 *
 * Handles the import submission to the backend API.
 */

import { useState } from 'react';
import { router } from '@inertiajs/react';
import type { ImportRequest, ImportResponse } from '@/types/recipeImport';

interface UseRecipeImportResult {
  importRecipe: (data: ImportRequest) => Promise<void>;
  isImporting: boolean;
  importError: string | null;
}

export function useRecipeImport(): UseRecipeImportResult {
  const [isImporting, setIsImporting] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);

  const importRecipe = async (data: ImportRequest) => {
    setIsImporting(true);
    setImportError(null);

    try {
      const response = await fetch('/food/api/recipe_import/import', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || '',
        },
        body: JSON.stringify(data),
      });

      const result: ImportResponse = await response.json();

      if (response.ok && result.success && result.redirect_url) {
        // Success - redirect to the new recipe page
        router.visit(result.redirect_url);
      } else {
        // Handle error
        if (result.errors) {
          // Field-specific errors
          const errorMessages = Object.entries(result.errors)
            .map(([field, messages]) => `${field}: ${messages.join(', ')}`)
            .join('; ');
          setImportError(errorMessages);
        } else if (result.error) {
          // General error
          setImportError(result.error);
        } else {
          setImportError('Import failed. Please try again.');
        }
        setIsImporting(false);
      }
    } catch (error) {
      setImportError('Network error. Please check your connection and try again.');
      setIsImporting(false);
    }
  };

  return {
    importRecipe,
    isImporting,
    importError,
  };
}
